import { useCallback, useEffect, useRef } from 'react'
import { useInvalidateReplyListQuery } from '../api/hooks/useInvalidateReplyListQuery'
import { useSocket } from '../api/websockets/useSocket'
import { useRequestsStore } from '../store/storeRequests'
import { ReplyDTO, ReplyStatus } from '../swagger/dto'

const updateDelay = 300
const chunksPerStep = 1

type UseRealTimeReplyUpdatesParams = {
    threadId: number
    scrollToStartOfLastMessage?: VoidFunction
    scrollToListEnd?: VoidFunction
}

export const useRealTimeReplyUpdates = ({
    threadId,
    scrollToStartOfLastMessage,
    scrollToListEnd,
}: UseRealTimeReplyUpdatesParams) => {
    const invalidateReplyList = useInvalidateReplyListQuery()

    const { requestMessages, setRequestMessages } = useRequestsStore((state) => state)

    const messages = useRef<Record<number, ReplyDTO[]>>({})
    const lastThinkingMessage = useRef<ReplyDTO | null>(null)
    const state = useRef<{
        messages: ReplyDTO[]
        scrollToStartOfLastMessage?: VoidFunction
        scrollToListEnd?: VoidFunction
    }>({ messages: [] })

    const handleMessage = useCallback((data: ReplyDTO) => {
        if (data.id) {
            const { id } = data
            const oldMessages = messages.current[id]

            messages.current = {
                ...messages.current,
                [id]: oldMessages ? [data, ...oldMessages] : [data],
            }
        }
    }, [])

    useSocket<ReplyDTO>(`collaboration_app/reply/ws/${threadId}`, handleMessage)

    state.current.messages = requestMessages ?? []
    state.current.scrollToStartOfLastMessage = scrollToStartOfLastMessage
    state.current.scrollToListEnd = scrollToListEnd

    useEffect(() => {
        const tick = (shouldProcessAllMessages = false) => {
            let isDataChanged = false
            let isLastMessageChanged = false
            let newRequestMessages = [...state.current.messages]

            const messagesForProcessing = Object.keys(messages.current)

            messagesForProcessing.forEach((requestId) => {
                const messagesById = messages.current[+requestId] ?? []
                let step =
                    messagesById.length >= chunksPerStep ? chunksPerStep : messagesById.length

                if (shouldProcessAllMessages) {
                    step = messagesById.length
                }

                while (step) {
                    const data = messagesById.pop()

                    if (data && data.thread_id === threadId) {
                        newRequestMessages = newRequestMessages.map((message) => {
                            const mainReplyId = data.parental_reply_id
                                ? data.parental_reply_id
                                : data.id

                            if (message.id === mainReplyId) {
                                if (data.parental_reply_id) {
                                    const { linked_replies } = message

                                    return {
                                        ...message,
                                        linked_replies: linked_replies?.length
                                            ? linked_replies.map((child) =>
                                                  child.id === data.id ? data : child,
                                              )
                                            : [data],
                                    }
                                } else {
                                    return data
                                }
                            }

                            return message
                        })

                        isDataChanged = true
                        const lastMessageId = newRequestMessages[0]?.id

                        if (lastMessageId === data.id) {
                            isLastMessageChanged = true

                            if (
                                data.status === ReplyStatus.ANSWER ||
                                data.status === ReplyStatus.THINKING
                            ) {
                                lastThinkingMessage.current = data
                            }
                        }

                        if (data.status === ReplyStatus.FINISHED) {
                            if (
                                isLastMessageChanged &&
                                lastMessageId !== lastThinkingMessage.current?.id
                            ) {
                                state.current.scrollToStartOfLastMessage?.()
                            }

                            invalidateReplyList()
                        }
                    } else {
                        delete messages.current[+requestId]
                    }

                    step--
                }
            })

            if (isDataChanged) {
                setRequestMessages(newRequestMessages, newRequestMessages.length)
                state.current.scrollToListEnd?.()
            }
        }

        let interval = setInterval(() => tick(), updateDelay)

        const onChangeVisibility = () => {
            if (document.hidden) {
                clearInterval(interval)
            } else {
                tick(true)
                interval = setInterval(() => tick(), updateDelay)
            }
        }

        document.addEventListener('visibilitychange', onChangeVisibility)

        return () => {
            clearInterval(interval)
            messages.current = {}
            document.removeEventListener('visibilitychange', onChangeVisibility)
        }
    }, [threadId])
}
